/**
 * デッキのカードを 1 行 10 枚のまま、画面に収まる大きさで並べる（デッキのページのカード・編集ページのレシピ。Issue #957）。
 * 並べる所の幅と、そこから画面の下までの高さを測り、メインとエクストラがスクロールなしで収まるカードの大きさから並びの幅を決める。
 * 狭い画面でも 10 枚のまま（2026-10-28 ユーザー判断。デッキの見た目を実物の並びと同じにする）。
 */
import { useLayoutEffect, useRef, useState } from 'react';

export const FIT_COLS = 10;
export const GAP = 3;

/**
 * 並びの幅（純関数）: 1 行 cols 枚のまま、メイン mainN 枚とエクストラ extraN 枚が高さ height に収まるカードの大きさから
 * 幅を決める（幅 width より広くはしない）。カードは 59:80、見出しは 1 段 labelH
 */
export function fitWidth(width, height, mainN, extraN, { cols = FIT_COLS, labelH = 22, minWidth = 240 } = {}) {
  if (!(width > 0) || !(height > 0)) return width > 0 ? width : null;
  const rows = Math.ceil(mainN / cols) + Math.ceil(extraN / cols);
  if (!rows) return width;
  const labels = (mainN ? 1 : 0) + (extraN ? 1 : 0);
  const cardH = (height - labels * labelH - (extraN ? 12 : 0) - rows * GAP) / rows;
  const w = Math.floor(cardH * (59 / 80) * cols + GAP * (cols - 1));
  return Math.max(Math.min(minWidth, width), Math.min(width, w));
}

/**
 * 並べる所に付ける ref（幅いっぱいの外側。ここで測る）と、中で真ん中に寄せる箱の style（見出しとグリッドを入れる。画面に収まる幅）、
 * グリッドの style（1 行 10 枚）
 */
export function useFitGrid(mainN, extraN, { outerRef = null, reserve = 0 } = {}) {
  // outerRef があれば幅はそこ（ページの幅いっぱい）から reserve（横に並ぶ検索など）を引いたもの。無ければ並べる所の幅
  const ref = useRef(null);
  const [box, setBox] = useState({ w: 0, h: 0 });
  // 毎回測る（窓の大きさ・上の欄の開け閉めで変わる）。同じなら描き直さない
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const measure = () => {
      const r = el.getBoundingClientRect();
      const width = outerRef?.current ? outerRef.current.clientWidth - reserve : el.clientWidth;
      const next = { w: Math.max(0, width), h: Math.max(0, Math.round(window.innerHeight - r.top - 16)) };
      setBox((prev) => (prev.w === next.w && prev.h === next.h ? prev : next));
    };
    measure();
    const ro = typeof ResizeObserver === 'function' ? new ResizeObserver(measure) : null;
    ro?.observe(el);
    if (outerRef?.current) ro?.observe(outerRef.current);
    window.addEventListener('resize', measure);
    return () => { ro?.disconnect(); window.removeEventListener('resize', measure); };
  });
  const w = fitWidth(box.w, box.h, mainN, extraN);
  return {
    ref,
    width: w,
    center: { margin: '0 auto', ...(w ? { maxWidth: w } : null) },
    grid: { display: 'grid', gap: GAP, marginBottom: 12, gridTemplateColumns: `repeat(${FIT_COLS}, 1fr)` },
  };
}
